import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Spinner, EmptyState } from "@/components/States";
import SurveyForm, { DISP_LABEL, DISP_COLOR } from "@/pages/survey/SurveyForm";
import { ClipboardList, CalendarClock, Target, Timer, Flame, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const COLOR = "#0F766E";

function Stat({ label, value, icon: Icon, sub, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /><span className="text-[11px] font-semibold uppercase tracking-wide">{label}</span></div>
      <p className="mt-1 font-head text-2xl font-extrabold text-slate-900">{value}</p>
      {sub && <p className="text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}

export default function SurveyDashboard() {
  const qc = useQueryClient();
  const [view, setView] = useState("call");
  const { data: meta } = useQuery({ queryKey: ["survey-meta"], queryFn: () => api.get("/survey/meta").then((r) => r.data) });
  const { data: stats } = useQuery({ queryKey: ["survey-stats"], queryFn: () => api.get("/survey/me/stats").then((r) => r.data), refetchInterval: 30000 });
  const { data: recent } = useQuery({ queryKey: ["survey-my"], queryFn: () => api.get("/survey/my").then((r) => r.data) });
  const { data: fus } = useQuery({ queryKey: ["survey-fus"], queryFn: () => api.get("/survey/followups").then((r) => r.data) });
  const refresh = () => ["survey-stats", "survey-my", "survey-fus"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const patchFu = async (id, payload) => {
    try { await api.patch(`/survey/followups/${id}`, payload); toast.success("Follow-up updated"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <DashboardShell title="Retailer Survey Console" roleColor={COLOR} roleLabel="Survey Agent">
      {stats && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="agent-stats">
          <Stat label="Today" value={`${stats.today}/${stats.daily_target}`} icon={Target} sub="surveys vs target" testid="stat-today" />
          <Stat label="This week" value={stats.week} icon={ClipboardList} testid="stat-week" />
          <Stat label="Interested" value={stats.interested} icon={Flame} sub="all-time" testid="stat-interested" />
          <Stat label="Avg handling" value={stats.aht_sec ? fmt(stats.aht_sec) : "—"} icon={Timer} sub="target ≤ 2:00" testid="stat-aht" />
          <Stat label="Follow-ups due" value={stats.followups_due} icon={CalendarClock} testid="stat-followups" />
        </div>
      )}

      <div className="mb-4 flex gap-2">
        {[["call", "New call"], ["followups", `Follow-ups${fus?.length ? ` (${fus.length})` : ""}`], ["recent", "My recent surveys"]].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`agent-tab-${v}`} className={`rounded-full px-4 py-1.5 text-xs font-bold ${view === v ? "text-white" : "border border-slate-200 bg-white text-slate-600"}`} style={view === v ? { background: COLOR } : {}}>{l}</button>
        ))}
      </div>

      {view === "call" && <SurveyForm meta={meta} onSaved={refresh} />}

      {view === "followups" && (
        !fus ? <Spinner /> : !fus.length ? <EmptyState icon={CalendarClock} title="No pending follow-ups" subtitle="Call-backs you schedule will appear here." /> : (
          <div className="space-y-2" data-testid="followup-list">
            {fus.map((f) => (
              <div key={f.id} className={`flex flex-wrap items-center gap-3 rounded-2xl border bg-white p-3 ${f.overdue ? "border-red-200" : f.due_today ? "border-amber-200" : "border-slate-200"}`} data-testid={`followup-${f.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800">{f.shop_name || "Unnamed shop"} <span className="text-xs font-normal text-slate-400">· {f.mobile}</span></p>
                  <p className="text-xs text-slate-500">{f.overdue ? <span className="font-bold text-red-500">Overdue · </span> : f.due_today ? <span className="font-bold text-amber-warm">Due today · </span> : null}{f.date}{f.note ? ` — ${f.note}` : ""}</p>
                </div>
                <input type="date" onChange={(e) => e.target.value && patchFu(f.id, { status: "rescheduled", new_date: e.target.value })} className="rounded-lg border border-slate-200 px-2 py-1 text-xs" title="Reschedule" data-testid={`fu-reschedule-${f.id}`} />
                <button onClick={() => patchFu(f.id, { status: "done" })} data-testid={`fu-done-${f.id}`} className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-xs font-bold text-white"><CheckCircle2 className="h-3.5 w-3.5" /> Done</button>
              </div>
            ))}
          </div>
        )
      )}

      {view === "recent" && (
        !recent ? <Spinner /> : !recent.length ? <EmptyState icon={ClipboardList} title="No surveys yet" subtitle="Your logged calls will appear here." /> : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="recent-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-400"><tr><th className="p-3">Time</th><th className="p-3">Shop</th><th className="p-3">Mobile</th><th className="p-3">Disposition</th><th className="p-3">Follow-up</th><th className="p-3">AHT</th></tr></thead>
              <tbody>{recent.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="p-3 text-xs text-slate-500">{s.created_at.slice(0, 16).replace("T", " ")}</td>
                  <td className="p-3 font-medium text-slate-800">{s.shop_name || "—"}{s.is_repeat && <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] font-bold text-amber-warm">repeat</span>}<p className="text-[11px] text-slate-400">{s.city}</p></td>
                  <td className="p-3 text-slate-600">{s.mobile}</td>
                  <td className="p-3"><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${DISP_COLOR[s.disposition]}`}>{DISP_LABEL[s.disposition]}</span></td>
                  <td className="p-3 text-xs text-slate-500">{s.follow_up_date || "—"}</td>
                  <td className="p-3 text-xs text-slate-500">{s.duration_sec ? fmt(s.duration_sec) : "—"}</td>
                </tr>))}</tbody>
            </table>
          </div>
        )
      )}
    </DashboardShell>
  );
}
